// ======================================================================
// 📁 ProductionPlanningGantt.js
// PURPOSE:
//   ERP-grade production timeline visualization
// ======================================================================

import React, { useMemo, useState } from "react";
import "./gantt.css";

//const PX_PER_MINUTE = 0.06; // visual density
const PX_PER_DAY  = 120;

export default function ProductionPlanningGantt({
  operations = [],
  selectedSeq,
  onSelect,
}) {
  const [zoom, setZoom] = useState(5);

  // ------------------------------------------------------------------
  // BUILD REAL CALENDAR-BASED GANTT MODEL
  // ------------------------------------------------------------------
  const gantt = useMemo(() => {
    if (!operations.length) return null;

    const starts = [];
    const ends = [];

    operations.forEach((op) => {
      const s = new Date(
        op.from ||
          op.planned_start_datetime ||
          op.suggested_start_datetime
      );

      const e = new Date(
        op.to ||
          op.planned_end_datetime ||
          op.suggested_end_datetime
      );

      if (!isNaN(s)) starts.push(s);
      if (!isNaN(e)) ends.push(e);
    });

    if (!starts.length || !ends.length) return null;

    const timelineStart = new Date(
      Math.min(...starts.map((d) => d.getTime()))
    );

    const timelineEnd = new Date(
      Math.max(...ends.map((d) => d.getTime()))
    );

    const totalMinutes =
      (timelineEnd.getTime() - timelineStart.getTime()) /
      60000;

    const rows = operations.map((op) => {
      const start = new Date(
        op.from ||
          op.planned_start_datetime ||
          op.suggested_start_datetime
      );

      const end = new Date(
        op.to ||
          op.planned_end_datetime ||
          op.suggested_end_datetime
      );

      if (isNaN(start) || isNaN(end)) {
        return null;
      }

      const offsetMinutes =
        (start.getTime() - timelineStart.getTime()) /
        60000;

      const durationMinutes =
        (end.getTime() - start.getTime()) / 60000;

      return {
        ...op,
        start,
        end,
        offsetMinutes: Math.max(offsetMinutes, 0),
        durationMinutes: Math.max(durationMinutes, 1),
      };
    }).filter(Boolean);

    return {
      timelineStart,
      timelineEnd,
      totalMinutes,
      rows,
    };
  }, [operations]);

  if (!gantt) return null;

  //const pxPerMinute = PX_PER_MINUTE * zoom;
    const pxPerMinute = (PX_PER_DAY / 1440) * zoom;

  const totalWidth = gantt.totalMinutes * pxPerMinute;

  // ------------------------------------------------------------------
  // BUILD DAY SCALE (DYNAMIC — NO HARD LIMIT)
  // ------------------------------------------------------------------
  const scaleDays = [];
  const cursor = new Date(gantt.timelineStart);

  while (cursor <= gantt.timelineEnd) {
    scaleDays.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  return (
    <div className="p-3" style={{ overflowX: "auto" }}>

      {/* ================= ZOOM ================= */}
      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
        <small>Zoom</small>
        <input
          type="range"
          min={2}
          max={20}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
        />
      </div>

      {/* ================= TIME SCALE ================= */}
      <div
        style={{
          display: "flex",
          marginLeft: 260,
          position: "sticky",
          top: 0,
          background: "#fff",
          zIndex: 2,
          fontSize: 12,
          width: totalWidth,
        }}
      >
        {scaleDays.map((d, i) => (
          <div
            key={i}
            style={{
              width: 1440 * pxPerMinute,
              borderLeft: "1px solid #d1d5db",
              paddingLeft: 6,
              whiteSpace: "nowrap",
            }}
          >
            {d.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
            })}
          </div>
        ))}
      </div>

      {/* ================= ROWS ================= */}
      <div style={{ position: "relative", width: totalWidth + 260 }}>

        {gantt.rows.map((op) => (
          <div
            key={op.seq}
            className="d-flex align-items-center mb-2"
            onClick={() => onSelect?.(op.seq)}
            style={{ cursor: "pointer" }}
          >
            {/* OP LABEL */}
            <div style={{ width: 260 }}>
              {op.seq}. {op.name}
            </div>

            {/* TRACK */}
            <div
              style={{
                position: "relative",
                height: 22,
                width: totalWidth,
                background: "#e5e7eb",
                borderRadius: 4,
              }}
            >
              {/* BAR */}
              <div
                style={{
                  position: "absolute",
                  left: op.offsetMinutes * pxPerMinute,
                  width:
                    op.durationMinutes * pxPerMinute,
                  height: "100%",
                  background:
                    selectedSeq === op.seq
                      ? "#16a34a"
                      : "#2563eb",
                  borderRadius: 4,
                  transition: "all 0.2s",
                }}
                title={`${op.start.toLocaleString()} → ${op.end.toLocaleString()}`}
              />
            </div>
          </div>
        ))}

      </div>
    </div>
  );
}
